import React, { FC, useEffect, useContext } from 'react';
import { useGoogleMaps } from 'react-hook-google-maps';
import {
  DataLayerContext,
  DataLayerContextType,
} from '../../providers/data-layer/data-layer.provider';
import GtmEventNameEnum from '../../../models/gtm-event-name-enum';
import { MapAndDirectionsType } from '../../../models/map-and-directions-type';
import IcomoonIcon from '../../atoms/icomoon-icon/icomoon-icon';
import TextLink from '../../atoms/text-link/text-link';
import RichText from '../../molecules/rich-text/rich-text';
import getTailwindConfig from '../../../helpers/get-tailwind-config';

const ControlPositionTopRight = 3;
const MapTypeIdRoadmap = 'roadmap';

const MapAndDirections: FC<MapAndDirectionsType> = ({
  address,
  addressHeading,
  directions,
  directionsHeading,
  getDirectionsLink,
  headline,
  locationLatitude,
  locationLongitude,
  googleMapAPI,
}: MapAndDirectionsType) => {
  const { pushToDataLayer } =
    useContext<DataLayerContextType>(DataLayerContext);
  const {
    ref: mapRef,
    map,
    google,
  } = useGoogleMaps(googleMapAPI, {
    mapTypeId: MapTypeIdRoadmap,
    disableDefaultUI: true,
    zoomControl: true,
    zoomControlOptions: {
      position: ControlPositionTopRight,
    },
    center: {
      lat: parseFloat(locationLatitude),
      lng: parseFloat(locationLongitude),
    },
    zoom: 15,
    styles: [
      {
        featureType: 'poi',
        elementType: 'labels',
        stylers: [{ visibility: 'off' }],
      },
    ],
  });

  useEffect(() => {
    if (google && map) {
      const frenchBlue =
        getTailwindConfig().theme.colors['french-blue'].DEFAULT;

      /*
       * The icon used below (path, scale and anchor properties) is
       * sourced from /public/icons/fonts/icomoon.svg.
       */

      // eslint-disable-next-line no-new
      new google.maps.Marker({
        icon: {
          path:
            'M512 0c-0.076 0-0.166 0-0.256 0-197.797 0-358.144 ' +
            '160.347-358.144 358.144 0 0.090 0 0.18 0 0.27v-0.014c0 268.8 ' +
            '358.4 665.6 358.4 665.6s358.4-396.8 358.4-665.6c0-0.076 0-0.166 ' +
            '0-0.256 0-197.797-160.347-358.144-358.144-358.144-0.090 0-0.18 ' +
            '0-0.27 0h0.014zM512 486.4c-70.692 0-128-57.308-128-128s57.308-128 ' +
            '128-128c70.692 0 128 57.308 128 128v0c0 70.692-57.308 128-128 128v0z',
          scale: 0.046,
          anchor: new google.maps.Point(512, 512),
          fillOpacity: 1,
          fillColor: frenchBlue,
          strokeWeight: 3,
          strokeColor: 'white',
        },
        position: map.center,
        map,
      });
    }
  }, [google, map]);

  return (
    <div className="py-component-spacing-mobile lg:py-component-spacing flex flex-wrap items-center">
      <div className="pb-8 md:pb-0 pl-0 md:pl-16 w-full md:w-1/2 md:order-2">
        <h4 className="pb-4 page-heading-four mb-6">{headline}</h4>
        {address && (
          <div className="flex mb-6">
            <div className="w-10">
              <IcomoonIcon name="pin" color="french-blue" size="lg" />
            </div>
            <div>
              {addressHeading && (
                <h6 className="page-heading-six mb-2">{addressHeading}</h6>
              )}
              <RichText className="text-grey-600">
                {address.replace(/\n/gi, '<br />')}
              </RichText>
            </div>
          </div>
        )}
        {directions && (
          <div className="flex mb-4">
            <div className="w-10">
              <IcomoonIcon name="car" color="french-blue" size="lg" />
            </div>
            <div>
              {directionsHeading && (
                <h6 className="page-heading-six mb-2">{directionsHeading}</h6>
              )}
              <RichText spacing="half" className="text-grey-600">
                {directions}
              </RichText>
            </div>
          </div>
        )}
        {getDirectionsLink && (
          <div className="flex items-center">
            <a
              href={getDirectionsLink.url}
              target={getDirectionsLink.target}
              rel="noreferrer"
              className="ml-8 mt-1 text-french-blue"
              onClick={() => {
                pushToDataLayer(GtmEventNameEnum.ContactDirections);
              }}
            >
              <IcomoonIcon
                name="open"
                size="md"
                className="mr-2"
                color="french-blue"
              />
            </a>
            <TextLink {...getDirectionsLink} />
          </div>
        )}
      </div>
      <div className="w-full md:w-1/2 md:order-1 ">
        <div className="relative w-full pt-full">
          {googleMapAPI && (
            <div
              ref={mapRef}
              className="absolute inset-0 rounded-lg overlfow-hidden"
            />
          )}
        </div>
      </div>
    </div>
  );
};

export default MapAndDirections;
